import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, catchError, finalize, map, shareReplay, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ErrorHandlingService } from '../general/error-handling.service';
import { ISimulationDetail } from '../../interfaces/self-serve/isimulation-details-information';

import { ISectionsWithFacts } from 'src/app/interfaces/self-serve/requests/ISectionsWithFacts-update';
import { IKnowledgeCheckQuestion } from 'src/app/interfaces/self-serve/responses/iknowledge-check-question';
import { IGuidedAnswerCreateRequest, IGuidedAnswerCreateResponse, IGuidedQuestionsWithAnswers } from 'src/app/interfaces/self-serve/responses/iguided-questions-with-answers';
import { IGuidedQuestionsUpdate } from 'src/app/interfaces/self-serve/requests/iguided-questions-update';
import { ISimulationPrompt } from '../../interfaces/self-serve/isimulation-prompt';
import { ISimulationUpdate } from 'src/app/interfaces/self-serve/requests/isimulation-update';
import { IFetchSimulationDetails } from 'src/app/interfaces/self-serve/responses/ifetch-simulation-details';
import { SelfServeLocalStorageUtil } from 'src/app/utils/local-storage.util';
import { ISimulationGenerationStatusResponse } from 'src/app/interfaces/self-serve/responses/isimulation-generation-status-response';
import { IPersonaInfo } from 'src/app/interfaces/self-serve/assignment-manager/ipersona-info';
import { IModule } from 'src/app/interfaces/self-serve/responses/imodule';
import { CallWordType } from 'src/app/types/call-word.types';
import { ICloneModuleKeywordsRequest } from 'src/app/interfaces/self-serve/requests/iclone-module-keywords-request';
import { IGetWordDetailsResponse } from 'src/app/interfaces/self-serve/responses/iget-word-details-response';
import { IWordsRequest } from 'src/app/interfaces/self-serve/requests/iwords-request';
import { IDeleteWordsRequest } from 'src/app/interfaces/self-serve/requests/idelete-words-request';
import { ISaveAsProductDefaultRequest } from 'src/app/interfaces/self-serve/requests/isave-as-product-default-request';
import { IDeleteCustomerContentRequest } from 'src/app/interfaces/self-serve/requests/idelete-customer-content-request';
import { IRetryFileValidationResponse } from 'src/app/interfaces/self-serve/responses/iretry-file-validation-response';
import { isContentLimitExceededError } from 'src/app/utils/document-upload-error.util';
import {
  IGeneratedCallDetails,
  IGeneratedCallDetailsResponse,
} from 'src/app/interfaces/self-serve/responses/igenerated-call-details';
import { mapGeneratedCallDetailsResponse } from 'src/app/utils/generated-call-details.util';
import { IRefinementFeedbackResponse } from 'src/app/interfaces/self-serve/responses/irefinement-feedback-response';
import { IEnableCallTestingResponse } from 'src/app/interfaces/self-serve/responses/ienable-call-testing-response';
import { ISkill } from 'src/app/interfaces/self-serve/iskill';
import { IMethodology } from 'src/app/interfaces/self-serve/imethodology';

export const SIMULATION_NAME_ALREADY_EXISTS_STATUS = 409;
export const DUPLICATE_WORD_STATUS = 409;

@Injectable({
  providedIn: 'root',
})
export class AuthorService {
  backendUrl: string = environment.backendUrl;

  /** getGeneratedCallDetails is read independently by every mounted Scoring-
   * tab section (skills, methodology, messaging, overall) plus the General
   * and What Users See tabs. They all react to the same refinement-completed
   * signal, so a single completion can trigger several of them in the same
   * tick — this coalesces those into one in-flight HTTP request instead of
   * one per caller. Cleared once the request settles, so the next refinement
   * still gets a fresh fetch rather than a stale cached one. */
  private pendingGeneratedCallDetails = new Map<string, Observable<IGeneratedCallDetails>>();

  constructor(
    private http: HttpClient,
    private errorHandlingService: ErrorHandlingService
  ) {}

  findAllSimulations(accountUuid: string, status?: string): Observable<any[]> {
    let url = `${this.backendUrl}/simulation/findAll?accountUuid=${accountUuid}`;
    if (status) {
      url += `&status=${encodeURIComponent(status)}`;
    }
    return this.http.get<any[]>(url).pipe(
      tap((res) => console.log('Fetched simulations:', res)),
      catchError((error) => {
        return this.errorHandlingService.handleError<any>(
          `/simulation/findAll?accountUuid=${accountUuid}${
            status ? `&status=${status}` : ''
          }`
        )(error);
      })
    );
  }

  findPublishedCallsByAccountUuid(accountUuid: string): Observable<any[]> {
    return this.http
      .get<any[]>(
        `${this.backendUrl}/call/findPublishedCallsByAccountUuid?accountUuid=${accountUuid}`
      )
      .pipe(
        tap((res) =>
          console.log('Fetched published calls by account uuid:', res)
        ),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/call/findPublishedCallsByAccountUuid?accountUuid=${accountUuid}`
          )(error);
        })
      );
  }

  getAccountList(): Observable<any> {
    return this.http
      .get<any>(`${this.backendUrl}/account/findAllAccountsByCustomer`)
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`account list: ${outcome}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/account/findAllAccountsByCustomer`
          )(error);
        })
      );
  }

  getSimulationGenerationStatus(
    callUuid: string,
    accountUuid: string
  ): Observable<ISimulationGenerationStatusResponse> {
    return this.http
      .get<ISimulationGenerationStatusResponse>(
        `${this.backendUrl}/simulation/getSimulationGenerationStatus`,
        { params: { callUuid, accountUuid } }
      )
      .pipe(
        catchError((error) => {
          return this.errorHandlingService.handleError<ISimulationGenerationStatusResponse>(
            `/simulation/getSimulationGenerationStatus?callUuid=${callUuid}&accountUuid=${accountUuid}`
          )(error);
        })
      );
  }

  getGeneratedCallDetails(callUuid: string): Observable<IGeneratedCallDetails> {
    const pending = this.pendingGeneratedCallDetails.get(callUuid);
    if (pending) return pending;

    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();

    const request$ = this.http
      .get<IGeneratedCallDetailsResponse>(
        `${this.backendUrl}/simulation/getSimulationOverview`,
        { params: { callUuid, accountUuid } }
      )
      .pipe(
        map(mapGeneratedCallDetailsResponse),
        catchError((error) => {
          return this.errorHandlingService.handleError<IGeneratedCallDetails>(
            `/simulation/getSimulationOverview?callUuid=${callUuid}&accountUuid=${accountUuid}`
          )(error);
        }),
        finalize(() => this.pendingGeneratedCallDetails.delete(callUuid)),
        shareReplay(1)
      );

    this.pendingGeneratedCallDetails.set(callUuid, request$);
    return request$;
  }

  requestCallRefinement(
    callUuid: string,
    feedback: string
  ): Observable<IRefinementFeedbackResponse> {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();

    return this.http
      .post<IRefinementFeedbackResponse>(
        `${this.backendUrl}/simulation/refinementFeedback`,
        { accountUuid, callUuid, feedback }
      )
      .pipe(
        catchError((error) => {
          return this.errorHandlingService.handleError<IRefinementFeedbackResponse>(
            `/simulation/refinementFeedback`
          )(error);
        })
      );
  }

  retryFileValidation(
    callUuid: string,
    accountUuid: string,
    customerContentUuids: string[]
  ): Observable<IRetryFileValidationResponse> {
    return this.http
      .post<IRetryFileValidationResponse>(
        `${this.backendUrl}/simulation/retryFileValidation`,
        { callUuid, accountUuid, customerContentUuids }
      )
      .pipe(
        catchError((error) => {
          return this.errorHandlingService.handleError<IRetryFileValidationResponse>(
            `/simulation/retryFileValidation`
          )(error);
        })
      );
  }

  createSimulationAPI(
    name: string,
    moduleUuid?: string,
    options?: {
      initialCustomerMessage?: string;
      customerContentUuids?: string[];
      tagUuids?: string[];
      callType?: string;
      hintsVisible?: boolean;
      referenceVisible?: boolean;
    }
  ): Observable<any> {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();

    const body: any = {
      name,
      accountUuid,
    };
    if (moduleUuid) {
      body.moduleUuid = moduleUuid;
    }
    if (options?.initialCustomerMessage) {
      body.initialCustomerMessage = options.initialCustomerMessage;
    }
    if (options?.customerContentUuids?.length) {
      body.customerContentUuids = options.customerContentUuids;
    }
    if (options?.tagUuids?.length) {
      body.tagUuids = options.tagUuids;
    }
    if (options?.callType) {
      body.callType = options.callType;
    }
    if (options?.hintsVisible !== undefined) {
      body.hintsVisible = options.hintsVisible;
    }
    if (options?.referenceVisible !== undefined) {
      body.referenceVisible = options.referenceVisible;
    }

    return this.http
      .post<any>(`${this.backendUrl}/simulation/create`, body)
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`simulationCreate: ${outcome}`);
        }),
        catchError((error) => {
          const backendMessage = typeof error?.error === 'string' ? error.error : error?.error?.message;
          if (isContentLimitExceededError(backendMessage)) {
            throw error;
          }
          if (error?.status === SIMULATION_NAME_ALREADY_EXISTS_STATUS) {
            throw error;
          }
          return this.errorHandlingService.handleError<any>(
            `/simulation/create`
          )(error);
        })
      );
  }

  updateSimulation(data: ISimulationUpdate): Observable<any> {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    const updatedData = { ...data, accountUuid };

    return this.http
      .post(`${this.backendUrl}/simulation/update`, updatedData)
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`update simulation: ${outcome}`);
        }),
        catchError((error) => {
          if (error?.status === SIMULATION_NAME_ALREADY_EXISTS_STATUS) {
            throw error;
          }
          return this.errorHandlingService.handleError<any>(
            `/simulation/update`
          )(error);
        })
      );
  }

  archiveSimulationAPI(callUuid: string): Observable<any> {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();

    return this.http
      .post<any>(`${this.backendUrl}/simulation/updateStatus`, {
        accountUuid,
        callUuid,
        targetStatus: 'ARCHIVED',
      })
      .pipe(
        tap(() => console.log(`Simulation ${callUuid} archived successfully`)),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/simulation/updateStatus`
          )(error);
        })
      );
  }

  deleteSimulationAPI(callUuid: string, moduleUuid: string): Observable<any> {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();

    const url = `${this.backendUrl}/simulation/delete?callUuid=${callUuid}&accountUuid=${accountUuid}&moduleUuid=${moduleUuid}`;
    return this.http.delete(url).pipe(
      tap(() => {
        console.log(`Simulation ${callUuid} deleted successfully`);
      }),
      catchError((error) => {
        return this.errorHandlingService.handleError<any>(
          `/simulation/delete?callUuid=${callUuid}&accountUuid=${accountUuid}&moduleUuid=${moduleUuid}`
        )(error);
      })
    );
  }

  duplicateSimulationAPI(payload: {
    callUuid: string;
    callTitle: string;
    moduleUuid: string;
  }): Observable<any> {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    const updatedData = { ...payload, accountUuid };

    return this.http
      .post(`${this.backendUrl}/simulation/duplicate`, updatedData)
      .pipe(
        tap((res) => {
          const outcome = res ? 'success' : 'failed';
          console.log(`Simulation duplicated: ${outcome}`);
        }),
        catchError((error) => {
          if (error?.status === SIMULATION_NAME_ALREADY_EXISTS_STATUS) {
            throw error;
          }
          return this.errorHandlingService.handleError<any>(
            `/simulation/duplicate`
          )(error);
        })
      );
  }

  createPersonaInfo(payload: {
    mode: string;
    callUuid: string;
    personaName: string;
    replicaId: string;
    gender?: string;
    welcomeMessage: string[];
  }): Observable<any> {
    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    return this.http
      .post(`${this.backendUrl}/tavus/createPersona`, { ...payload, accountUuid })
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'success' : 'failed';
          console.log(`Persona creation: ${outcome}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/tavus/createPersona`
          )(error);
        })
      );
  }

  updatePersonaInfo(payload: IPersonaInfo): Observable<any> {
    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    return this.http
      .post(`${this.backendUrl}/tavus/updatePersona`, { ...payload, accountUuid })
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'success' : 'failed';
          console.log(`Persona updation: ${outcome}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/tavus/updatePersona`
          )(error);
        })
      );
  }

  publishSimulation(callUuid: string, moduleUuid: string): Observable<any> {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();

    const url = `${this.backendUrl}/simulation/publish?callUuid=${callUuid}&accountUuid=${accountUuid}&moduleUuid=${moduleUuid}`;
    return this.http
      .get(url, {
        responseType: 'text',
      })
      .pipe(
        tap(() =>
          console.log(`Simulation published for callUuid: ${callUuid}`)
        ),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/simulation/publish?callUuid=${callUuid}&accountUuid=${accountUuid}&moduleUuid=${moduleUuid}`
          )(error);
        })
      );
  }

  getAvatarReplicas(): Observable<any> {
    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    return this.http
      .get<any>(`${this.backendUrl}/tavus/getPublishedReplicas?accountUuid=${accountUuid}`)
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`getPublishedReplicas: ${outcome}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/tavus/getPublishedReplicas`
          )(error);
        })
      );
  }

  getAttempts(callUuid: string, moduleUuid: string) {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();

    return this.http
      .get<any>(
        `${this.backendUrl}/simulation/viewAttempts?callUuid=${callUuid}&accountUuid=${accountUuid}&moduleUuid=${moduleUuid}`
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`viewAttempts: ${outcome}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/simulation/viewAttempts?callUuid=${callUuid}&accountUuid=${accountUuid}&moduleUuid=${moduleUuid}`
          )(error);
        })
      );
  }

  exportCallDetails(callUuid: string): Observable<Blob> {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();

    return this.http
      .get<Blob>(
        `${this.backendUrl}/simulation/exportCallDetails?callUuid=${callUuid}&accountUuid=${accountUuid}`,
        { responseType: 'blob' as 'json' }
      )
      .pipe(
        catchError((error) => {
          return this.errorHandlingService.handleError<Blob>(
            `/simulation/exportCallDetails?callUuid=${callUuid}&accountUuid=${accountUuid}`
          )(error);
        })
      );
  }

  getUsersForTesting(accountUuid: string) {
    return this.http
      .get<any>(
        `${this.backendUrl}/user/getUsersInfoByAccount?accountUuid=${accountUuid}`
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`getUsersInfoByAccount: ${outcome}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/user/getUsersInfoByAccount?accountUuid=${accountUuid}`
          )(error);
        })
      );
  }

  sendTestAssignment(
    callUuid: string,
    userUuids: string[],
    moduleUuid: string
  ) {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    return this.http
      .post<any>(`${this.backendUrl}/simulation/sendTestAssignment`, {
        callUuid,
        userUuids,
        accountUuid,
        moduleUuid,
      })
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'posted' : 'did not post';
          console.log(`sendTestAssignment: ${outcome}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/simulation/sendTestAssignment?callUuid=${callUuid}&accountUuid=${accountUuid}&moduleUuid=${moduleUuid}`
          )(error);
        })
      );
  }

  enableCallTesting(
    callUuid: string,
    userUuids: string[],
    moduleUuid: string
  ): Observable<IEnableCallTestingResponse> {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    return this.http
      .post<IEnableCallTestingResponse>(`${this.backendUrl}/simulation/enableCallTesting`, {
        callUuid,
        userUuids,
        accountUuid,
        moduleUuid,
      })
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'posted' : 'did not post';
          console.log(`enableCallTesting: ${outcome}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<IEnableCallTestingResponse>(
            `/simulation/enableCallTesting?callUuid=${callUuid}&accountUuid=${accountUuid}&moduleUuid=${moduleUuid}`
          )(error);
        })
      );
  }

  removeAssignment(
    callUuid: string,
    userUuid: string,
    moduleUuid: string
  ): Observable<any> {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();

    return this.http
      .delete(
        `${this.backendUrl}/simulation/removeAssignment?callUuid=${callUuid}&userUuid=${userUuid}&accountUuid=${accountUuid}&moduleUuid=${moduleUuid}`
      )
      .pipe(
        tap(() => console.log(`Assignment removed`)),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/simulation/removeAssignment?callUuid=${callUuid}&userUuid=${userUuid}&accountUuid=${accountUuid}&moduleUuid=${moduleUuid}`
          )(error);
        })
      );
  }

  createSimulationDetails(
    callUuid: string,
    callDetails: ISimulationDetail[]
  ): Observable<any> {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();

    return this.http
      .post(`${this.backendUrl}/simulation/createDetails`, {
        callUuid,
        callDetails,
        accountUuid,
      })
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`create simulation details: ${outcome}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/simulation/createDetails`
          )(error);
        })
      );
  }

  updateSimulationDetails(
    callDetails: ISimulationDetail[],
    callUuid?: string
  ): Observable<any> {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();

    return this.http
      .post(`${this.backendUrl}/simulation/updateDetails`, {
        callDetails,
        accountUuid,
        ...(callUuid ? { callUuid } : {}),
      })
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`update simulation details: ${outcome}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/simulation/updateDetails`
          )(error);
        })
      );
  }

  createSimulationPrompts(
    callUuid: string,
    prompts: ISimulationPrompt[]
  ): Observable<any> {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();

    return this.http
      .post(`${this.backendUrl}/simulation/createPrompts`, {
        callUuid,
        prompts,
        accountUuid,
      })
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`create simulation prompts: ${outcome}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/simulation/createPrompts`
          )(error);
        })
      );
  }

  updateSimulationPrompts(
    callUuid: string,
    prompts: ISimulationPrompt[]
  ): Observable<any> {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    return this.http
      .post(`${this.backendUrl}/simulation/updatePrompts`, {
        callUuid,
        prompts,
        accountUuid,
      })
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`update simulation prompts: ${outcome}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/simulation/updatePrompts`
          )(error);
        })
      );
  }

  generateSttPrompt(callUuid: string): Observable<ISimulationPrompt> {
    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    return this.http
      .post(
        `${this.backendUrl}/prompt/generateSttPrompt?callUuid=${callUuid}&accountUuid=${accountUuid}`,
        {}
      )
      .pipe(
        map((result: any) => result),
        tap((res) => {
          const outcome = res ? 'generated' : 'did not generate';
          console.log(`generate STT prompt: ${outcome}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/prompt/generateSttPrompt?callUuid=${callUuid}`
          )(error);
        })
      );
  }

  getSimulationDetails(callUuid: string, moduleUuid: string) {
    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    return this.http
      .get<any>(
        `${this.backendUrl}/simulation/getSimulationDetails?callUuid=${callUuid}&moduleUuid=${moduleUuid}&accountUuid=${accountUuid}`
      )
      .pipe(
        map((res: IFetchSimulationDetails) => {
          // Normalize welcomeMessage to an array
          if (res?.callPersona) {
            const wm = res.callPersona.welcomeMessage;

            if (Array.isArray(wm)) {
              res.callPersona.welcomeMessage = wm;
            } else if (typeof wm === 'string') {
              res.callPersona.welcomeMessage = [wm];
            } else {
              res.callPersona.welcomeMessage = [''];
            }
          }

          return res;
        }),
        tap((res: IFetchSimulationDetails) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`Simulation Details: ${outcome}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/simulation/getSimulationDetails?callUuid=${callUuid}&moduleUuid=${moduleUuid}`
          )(error);
        })
      );
  }

  getSectionsWithFacts(callUuid: string) {
    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    return this.http
      .get<any>(
        `${this.backendUrl}/callFact/findSectionsWithFactsByCallUuid?callUuid=${callUuid}&accountUuid=${accountUuid}`
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`Sections with facts: ${outcome}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/callFact/findSectionsWithFactsByCallUuid?callUuid=${callUuid}`
          )(error);
        })
      );
  }

  createSectionWithFacts(
    callUuid: string,
    prompts: ISectionsWithFacts
  ): Observable<any> {
    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    return this.http
      .post(`${this.backendUrl}/callFact/createSectionWithFacts`, {
        callUuid,
        ...prompts,
        accountUuid,
      })
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`Create sections with facts: ${outcome}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/callFact/createSectionWithFacts`
          )(error);
        })
      );
  }

  updateSectionWithFacts(
    callUuid: string,
    sections: ISectionsWithFacts
  ): Observable<any> {
    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    return this.http
      .post(`${this.backendUrl}/callFact/updateSectionWithFacts`, {
        callUuid,
        ...sections,
        accountUuid,
      })
      .pipe(
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/callFact/updateSectionWithFacts`
          )(error);
        })
      );
  }

  getGuidedQuestionsWithAnswers(callUuid: string): Observable<IGuidedQuestionsWithAnswers> {
    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    return this.http
      .get<IGuidedQuestionsWithAnswers>(
        `${this.backendUrl}/simulation/getGuidedQuestionsWithAnswers?callUuid=${callUuid}&accountUuid=${accountUuid}`
      )
      .pipe(
        catchError((error) => {
          return this.errorHandlingService.handleError<IGuidedQuestionsWithAnswers>(
            `/simulation/getGuidedQuestionsWithAnswers?callUuid=${callUuid}`
          )(error);
        })
      );
  }

  createKnowledgeCheckQuestion(
    callUuid: string,
    question: Partial<IKnowledgeCheckQuestion>
  ): Observable<IKnowledgeCheckQuestion> {
    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    return this.http
      .post<IKnowledgeCheckQuestion>(`${this.backendUrl}/simulation/createKnowledgeCheckQuestion`, {
        accountUuid,
        callUuid,
        ...question,
      })
      .pipe(
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`Create Knowledge Check question: ${outcome}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<IKnowledgeCheckQuestion>(
            `/simulation/createKnowledgeCheckQuestion`
          )(error);
        })
      );
  }

  updateGuidedQuestionsWithAnswers(
    callUuid: string,
    request: IGuidedQuestionsUpdate
  ): Observable<IGuidedQuestionsUpdate> {
    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    return this.http
      .post<IGuidedQuestionsUpdate>(`${this.backendUrl}/simulation/updateGuidedQuestionsWithAnswers`, {
        accountUuid,
        callUuid,
        ...request,
      })
      .pipe(
        catchError((error) => {
          return this.errorHandlingService.handleError<IGuidedQuestionsUpdate>(
            `/simulation/updateGuidedQuestionsWithAnswers`
          )(error);
        })
      );
  }

  deleteKnowledgeCheckQuestion(callUuid: string, questionUuid: string): Observable<string> {
    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    return this.http
      .delete(
        `${this.backendUrl}/simulation/deleteKnowledgeCheckQuestion?accountUuid=${accountUuid}&callUuid=${callUuid}&questionUuid=${questionUuid}`,
        { responseType: 'text' }
      )
      .pipe(
        tap(() => console.log(`Knowledge Check question ${questionUuid} deleted`)),
        catchError((error) => {
          return this.errorHandlingService.handleError<string>(
            `/simulation/deleteKnowledgeCheckQuestion?callUuid=${callUuid}&questionUuid=${questionUuid}`
          )(error);
        })
      );
  }

  deleteSection(callUuid: string, sectionUuid: string): Observable<any> {
    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    return this.http
      .delete(
        `${this.backendUrl}/callFact/deleteSection?callUuid=${callUuid}&sectionUuid=${sectionUuid}&accountUuid=${accountUuid}`,
        {
          responseType: 'text',
        }
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`Delete section: ${outcome}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/callFact/deleteSection?callUuid=${callUuid}&sectionUuid=${sectionUuid}`
          )(error);
        })
      );
  }

  deleteSectionFact(callUuid: string, factUuid: string): Observable<any> {
    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    return this.http
      .delete(
        `${this.backendUrl}/callFact/deleteFact?callUuid=${callUuid}&factUuid=${factUuid}&accountUuid=${accountUuid}`,
        {
          responseType: 'text',
        }
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`Delete section facts: ${outcome}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/callFact/deleteFact?callUuid=${callUuid}&factUuid=${factUuid}`
          )(error);
        })
      );
  }

  createGuidedAnswer(callUuid: string, payload: IGuidedAnswerCreateRequest): Observable<IGuidedAnswerCreateResponse> {
    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    return this.http
      .post<IGuidedAnswerCreateResponse>(`${this.backendUrl}/simulation/createGuidedAnswer`, {
        accountUuid,
        callUuid,
        ...payload,
      })
      .pipe(
        catchError((error) => {
          return this.errorHandlingService.handleError<IGuidedAnswerCreateResponse>(
            `/simulation/createGuidedAnswer`
          )(error);
        })
      );
  }

  // TODO SR-21514: implement when API is ready
  updateAnswerPoint(_callUuid: string, _answerPointUuid: string, _answerPoint: { verb: string; displayText: string; displayFactIncorrect: string | null }): Observable<void> {
    return new Observable((observer) => {
      observer.next();
      observer.complete();
    });
  }

  deleteAnswerPoint(callUuid: string, callGuidedUuid: string, answerUuid: string): Observable<string> {
    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    return this.http
      .delete(
        `${this.backendUrl}/simulation/deleteGuidedAnswer?accountUuid=${accountUuid}&callUuid=${callUuid}&callGuidedUuid=${callGuidedUuid}&answerUuid=${answerUuid}`,
        { responseType: 'text' }
      )
      .pipe(
        catchError((error) => {
          return this.errorHandlingService.handleError<string>(
            `/simulation/deleteGuidedAnswer?callUuid=${callUuid}&callGuidedUuid=${callGuidedUuid}&answerUuid=${answerUuid}`
          )(error);
        })
      );
  }

  getModuleList(accountUuid: string): Observable<IModule[]> {
    if (!accountUuid) {
      throw new Error('accountUuid is required');
    }

    return this.http
      .get<IModule[]>(
        `${this.backendUrl}/module/findByAccountUuid?accountUuid=${accountUuid}`
      )
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'fetched' : 'did not find';
          console.log(`getModuleList: ${outcome}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<IModule[]>(
            `/module/findByAccountUuid?accountUuid=${accountUuid}`
          )(error);
        })
      );
  }

  createModule(name: string): Observable<IModule> {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();

    return this.http
      .post<IModule>(`${this.backendUrl}/module/create`, {
        accountUuid,
        name,
      })
      .pipe(
        map((result) => result),
        tap((res) => {
          const outcome = res ? 'created' : 'creation failed';
          console.log(`createModule: ${outcome}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<IModule>(
            `/module/create`
          )(error);
        })
      );
  }
  createCallBackground(callUuid: string, imageFile: File): Observable<string> {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();

    const formData = new FormData();
    formData.append('call_uuid', callUuid);
    formData.append('account_uuid', accountUuid);
    formData.append('image', imageFile);

    return this.http
      .post(`${this.backendUrl}/call/background/create`, formData, {
        responseType: 'text',
      })
      .pipe(
        tap((s3Url) => {
          console.log(`Background created successfully. S3 URL: ${s3Url}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<string>(
            `/call/background/create`
          )(error);
        })
      );
  }

  uploadCustomerContent(accountUuid: string, file: File): Observable<{ customerContentUuid: string }> {
    const formData = new FormData();
    formData.append('document', file);
    const params = new HttpParams().set('account_uuid', accountUuid);

    return this.http
      .post<{ customerContentUuid: string }>(
        `${this.backendUrl}/customerContent/upload`,
        formData,
        { params }
      )
      .pipe(
        catchError((error) => {
          return this.errorHandlingService.handleError<{ customerContentUuid: string }>(
            `/customerContent/upload`
          )(error);
        })
      );
  }

  deleteCustomerContent(
    accountUuid: string,
    payload: IDeleteCustomerContentRequest
  ): Observable<string> {
    return this.http
      .request(
        'DELETE',
        `${this.backendUrl}/customerContent/delete?account_uuid=${accountUuid}`,
        {
          body: payload,
          responseType: 'text',
        }
      )
      .pipe(
        map((res) => res as string),
        catchError((error) => {
          return this.errorHandlingService.handleError<string>(
            `/customerContent/delete`
          )(error);
        })
      );
  }

  createCallThumbnail(callUuid: string, imageFile: File): Observable<string> {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();

    const formData = new FormData();
    formData.append('call_uuid', callUuid);
    formData.append('account_uuid', accountUuid);
    formData.append('image', imageFile);

    return this.http
      .post(`${this.backendUrl}/call/thumbnail/create`, formData, {
        responseType: 'text',
      })
      .pipe(
        tap((s3Url) => {
          console.log(`Thumbnail created successfully. S3 URL: ${s3Url}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<string>(
            `/call/thumbnail/create`
          )(error);
        })
      );
  }

  resetCallKeywords(
    callUuid: string,
    wordType: CallWordType,
    personaUuid?: string
  ): Observable<any> {
    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    return this.http
      .post(
        `${this.backendUrl}/word/resetKeywords`,
        {
          callUuid,
          wordType,
          personaUuid,
          accountUuid,
        },
        {
          responseType: 'text',
        }
      )
      .pipe(
        tap((res) => {
          const outcome = res ? 'reset' : 'reset failed';
          console.log(`resetKeywords: ${outcome}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<string>(
            `/word/resetKeywords`
          )(error);
        })
      );
  }

  cloneModuleKeywords(payload: ICloneModuleKeywordsRequest): Observable<any> {
    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    return this.http
      .post(`${this.backendUrl}/word/cloneModuleKeywords`, { ...payload, accountUuid }, {
        responseType: 'text',
      })
      .pipe(
        tap((res) => {
          const outcome = res ? 'cloned' : 'clone failed';
          console.log(`cloneModuleKeywords: ${outcome}`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<string>(
            `/word/cloneModuleKeywords`
          )(error);
        })
      );
  }

  getWordDetails(
    callUuid: string,
    personaUuid?: string
  ): Observable<IGetWordDetailsResponse> {
    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    let url = `${this.backendUrl}/word/getWordDetails?callUuid=${callUuid}${
      personaUuid ? `&personaUuid=${personaUuid}` : ''
    }&accountUuid=${accountUuid}`;

    return this.http.get<IGetWordDetailsResponse>(url).pipe(
      map((result) => result),
      tap((res) => {
        const outcome = res ? 'fetched' : 'did not find';
        console.log(`getWordDetails: ${outcome}`);
      }),
      catchError((error) => {
        return this.errorHandlingService.handleError<IGetWordDetailsResponse>(
          `/word/getWordDetails?callUuid=${callUuid}${
            personaUuid ? `&personaUuid=${personaUuid}` : ''
          }`
        )(error);
      })
    );
  }

  createWords(payload: IWordsRequest): Observable<string> {
    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    return this.http
      .post(`${this.backendUrl}/word/createWords`, { ...payload, accountUuid }, {
        responseType: 'text',
      })
      .pipe(
        tap((res) => {
          console.log(`createWords: created successfully`);
        }),
        catchError((error) => {
          if (error?.status === DUPLICATE_WORD_STATUS) {
            throw error;
          }
          return this.errorHandlingService.handleError<string>(
            `/word/createWords`
          )(error);
        })
      );
  }

  updateWords(payload: IWordsRequest): Observable<string> {
    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    return this.http
      .post(`${this.backendUrl}/word/updateWords`, { ...payload, accountUuid }, {
        responseType: 'text',
      })
      .pipe(
        tap((res) => {
          console.log(`updateWords: updated successfully`);
        }),
        catchError((error) => {
          if (error?.status === DUPLICATE_WORD_STATUS) {
            throw error;
          }
          return this.errorHandlingService.handleError<string>(
            `/word/updateWords`
          )(error);
        })
      );
  }

  deleteWords(payload: IDeleteWordsRequest): Observable<string> {
    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    return this.http
      .request('DELETE', `${this.backendUrl}/word/deleteWords`, {
        body: { ...payload, accountUuid },
        responseType: 'text',
      })
      .pipe(
        map((res) => res as string),
        tap((_res) => {
          console.log(`deleteWords: deleted successfully`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<string>(
            `/word/deleteWords`
          )(error);
        })
      );
  }

  updateSkillSelection(callUuid: string, skillUuid: string | null): Observable<any> {
    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    return this.http
      .post<any>(`${this.backendUrl}/simulation/updateSkillSelection`, {
        callUuid,
        accountUuid,
        skillUuid: skillUuid || null,
      })
      .pipe(
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/simulation/updateSkillSelection`
          )(error);
        })
      );
  }

  findActiveSkillsByAccount(callUuid: string): Observable<ISkill[]> {
    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    return this.http
      .get<ISkill[]>(`${this.backendUrl}/skill/findActiveByAccount`, {
        params: { accountUuid, callUuid },
      })
      .pipe(
        catchError((error) => {
          return this.errorHandlingService.handleError<ISkill[]>(
            `/skill/findActiveByAccount`
          )(error);
        })
      );
  }

  findActiveMethodologiesByAccount(callUuid: string): Observable<IMethodology[]> {
    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    return this.http
      .get<IMethodology[]>(`${this.backendUrl}/methodology/findActiveByAccount`, {
        params: { accountUuid, callUuid },
      })
      .pipe(
        catchError((error) => {
          return this.errorHandlingService.handleError<IMethodology[]>(
            `/methodology/findActiveByAccount`
          )(error);
        })
      );
  }

  selectMethodology(callUuid: string, methodologyUuid: string | null): Observable<any> {
    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    return this.http
      .post<any>(`${this.backendUrl}/simulation/selectMethodology`, {
        callUuid,
        accountUuid,
        methodologyUuid: methodologyUuid || null,
      })
      .pipe(
        catchError((error) => {
          return this.errorHandlingService.handleError<any>(
            `/simulation/selectMethodology`
          )(error);
        })
      );
  }

  overrideConversePrompt(
    accountUuid: string,
    callUuid: string,
    promptText: string
  ): Observable<{ promptUuid: string; version: number }> {
    return this.http
      .post<{ promptUuid: string; version: number }>(
        `${this.backendUrl}/simulation/overrideConversePrompt`,
        { accountUuid, callUuid, promptText }
      )
      .pipe(
        catchError((error) => {
          return this.errorHandlingService.handleError<{ promptUuid: string; version: number }>(
            `/simulation/overrideConversePrompt`
          )(error);
        })
      );
  }

  saveAsProductDefault(
    payload: ISaveAsProductDefaultRequest
  ): Observable<string> {
    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    return this.http
      .post(`${this.backendUrl}/word/saveAsProductDefault`, { ...payload, accountUuid }, {
        responseType: 'text',
      })
      .pipe(
        tap((_res) => {
          console.log(`saveAsProductDefault: saved successfully`);
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError<string>(
            `/word/saveAsProductDefault`
          )(error);
        })
      );
  }
}
